if (!customElements.get('zoom-dialog')) {
  class ZoomDialog extends HTMLElement {
    constructor() {
      super();
      this.dialog = this.querySelector('dialog');
      this.media = Array.from(this.querySelectorAll('.product-media-container'));
      this.thumbnails = this.querySelector('.dialog-thumbnails-list');
      this.highResImagesLoaded = new Set();
      this.zoomStates = new WeakMap();

      this.handleScroll = this.debounce(this.handleScrollEvent.bind(this), 50);

      if (this.dialog) {
        this.dialog.addEventListener('scroll', this.handleScroll);
      }
    }

    connectedCallback() {
      this.querySelectorAll('[data-zoom-index]').forEach(trigger => {
        trigger.addEventListener('click', e => {
          const index = Number(trigger.dataset.zoomIndex);

          this.open(index, e);
        });
      });
      this.querySelectorAll('[data-zoom-action]').forEach(button => {
        button.addEventListener('click', e => this.handleZoomAction(e));
      });
      this.querySelectorAll('[data-zoom-nav]').forEach(button => {
        button.addEventListener('click', e => this.handleNavAction(e));
      });
      this.setupDragZoom();
    }

    disconnectedCallback() {
      if (this.dialog) {
        this.dialog.removeEventListener('scroll', this.handleScroll);
      }
    }

    async open(index, event) {
      event.preventDefault();
      this.activeIndex = index;
      this.resetAllZoom();

      const targetImage = this.media[index];
      const targetThumbnail = this.thumbnails?.children[index];

      const openDialog = () => {
        if (this.dialog.showModal) {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        for (const target of [targetThumbnail, targetImage]) {
          target?.scrollIntoView({ behavior: 'smooth' });
        }
      };

      const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

      if (!this.supportsViewTransitions() || !sourceImage || !targetImage) {
        openDialog();
        this.selectThumbnail(index, { behavior: 'auto' });
        this.updateProgress();
        return;
      }

      const transitionName = `gallery-item`;
      sourceImage.style.setProperty('view-transition-name', transitionName);

      await this.startViewTransition(() => {
        openDialog();
        sourceImage.style.removeProperty('view-transition-name');
        targetImage.style.setProperty('view-transition-name', transitionName);
      });

      targetImage.style.removeProperty('view-transition-name');
      this.selectThumbnail(index, { behavior: 'auto' });
      this.updateProgress();
    }

    closeDialog() {
      if (this.dialog.close) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      window.dispatchEvent(new CustomEvent('dialog-close'));
    }

    async close() {
      if (!this.supportsViewTransitions()) return this.closeDialog();

      const mostVisibleElement = await this.getMostVisibleElement(this.media);
      const activeIndex = this.media.indexOf(mostVisibleElement);
      const transitionName = `gallery-item`;

      const mediaGallery = this.closest('media-gallery');
      const slideshowActive = mediaGallery?.presentation === 'carousel';
      const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

      if (!slide) return this.closeDialog();

      this.dialog.classList.add('dialog--closed');
      await this.onAnimationEnd(this.thumbnails);

      mostVisibleElement.style.setProperty('view-transition-name', transitionName);

      await this.startViewTransition(() => {
        mostVisibleElement.style.removeProperty('view-transition-name');
        slide.style.setProperty('view-transition-name', transitionName);
        this.closeDialog();
      });

      slide.style.removeProperty('view-transition-name');
      this.dialog.classList.remove('dialog--closed');
    }

    async handleScrollEvent() {
      const mostVisibleElement = await this.getMostVisibleElement(this.media);
      const activeIndex = this.media.indexOf(mostVisibleElement);
      this.activeIndex = activeIndex;
      const targetThumbnail = this.thumbnails?.children[activeIndex];

      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

      Array.from(this.thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === activeIndex}`);
      });

      this.loadHighResolutionImage(mostVisibleElement);
      this.dispatchEvent(new CustomEvent('zoom-media:selected', { detail: { index: activeIndex } }));
      this.updateProgress();
    }

    async handleThumbnailClick(index) {
      const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';
      this.selectThumbnail(index, { behavior });
    }

    async selectThumbnail(index, options = { behavior: 'smooth' }) {
      if (!this.thumbnails || !this.thumbnails.children.length) return;
      if (isNaN(index) || index < 0 || index >= this.thumbnails.children.length) return;
      this.activeIndex = index;
      this.resetAllZoom();

      const targetThumbnail = this.thumbnails.children[index];
      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

      Array.from(this.thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      });

      this.scrollIntoView(targetThumbnail, {
        ancestor: this.thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });

      const targetImage = this.media[index];

      if (targetImage) {
        targetImage.scrollIntoView({
          behavior: options.behavior,
        });

        this.loadHighResolutionImage(targetImage);
      }

      this.dispatchEvent(new CustomEvent('zoom-media-selected', { detail: { index } }));
      this.updateProgress();
    }

    handleZoomAction(event) {
      event.preventDefault();
      const button = event.currentTarget;
      if (!button || !(button instanceof HTMLElement)) return;
      const action = button.dataset.zoomAction;
      const target = this.getActiveZoomTarget();
      if (!target) return;
      const current = this.getZoomScale(target);
      const step = 0.2;
      let next = current;
      if (action === 'in') next = current + step;
      if (action === 'out') next = current - step;
      next = Math.max(1, Math.min(next, 3));
      this.setZoomScale(target, next);
    }

    handleNavAction(event) {
      event.preventDefault();
      const button = event.currentTarget;
      if (!button || !(button instanceof HTMLElement)) return;
      const direction = button.dataset.zoomNav;
      const total = this.media?.length || 0;
      if (!total) return;
      const current = typeof this.activeIndex === 'number' ? this.activeIndex : 0;
      const nextIndex = direction === 'prev' ? (current - 1 + total) % total : (current + 1) % total;
      this.selectThumbnail(nextIndex, { behavior: 'smooth' });
    }

    updateProgress() {
      const bar = this.querySelector('.dialog-zoom-progress__bar');
      if (!bar) return;
      const total = this.media?.length || 0;
      if (!total) {
        bar.style.width = '0%';
        return;
      }
      const index = typeof this.activeIndex === 'number' ? this.activeIndex : 0;
      const ratio = total <= 1 ? 1 : (index + 1) / total;
      bar.style.width = `${Math.round(ratio * 100)}%`;
    }

    getActiveZoomTarget() {
      const index = typeof this.activeIndex === 'number' ? this.activeIndex : 0;
      const container = this.media?.[index];
      if (!container) return null;
      return container.querySelector('.product-media__drag-zoom-wrapper');
    }

    getZoomScale(target) {
      const value = target.style.getPropertyValue('--drag-zoom-scale');
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 1;
    }

    setZoomScale(target, value) {
      target.style.setProperty('--drag-zoom-scale', value.toFixed(2));
      this.toggleZoomedState(value > 1);
      this.clampZoomTranslate(target);
      target.style.touchAction = value > 1 ? 'none' : 'pan-x pan-y';
    }

    toggleZoomedState(isZoomed) {
      const gallery = this.querySelector('.dialog-zoomed-gallery');
      if (!gallery) return;
      gallery.classList.toggle('is-zoomed', isZoomed);
    }

    setupDragZoom() {
      this.querySelectorAll('.product-media__drag-zoom-wrapper').forEach(wrapper => {
        this.initZoomState(wrapper);
        wrapper.style.touchAction = 'pan-x pan-y';
        wrapper.addEventListener('pointerdown', e => this.onPointerDown(e, wrapper));
        wrapper.addEventListener('pointermove', e => this.onPointerMove(e, wrapper));
        wrapper.addEventListener('pointerup', e => this.onPointerUp(e, wrapper));
        wrapper.addEventListener('pointercancel', e => this.onPointerUp(e, wrapper));
        wrapper.addEventListener('touchstart', e => this.onTouchStart(e, wrapper), { passive: false });
        wrapper.addEventListener('touchmove', e => this.onTouchMove(e, wrapper), { passive: false });
        wrapper.addEventListener('touchend', e => this.onTouchEnd(e, wrapper));
      });
    }

    initZoomState(wrapper) {
      if (this.zoomStates.has(wrapper)) return;
      this.zoomStates.set(wrapper, {
        scale: 1,
        translateX: 0,
        translateY: 0,
        isPanning: false,
        startX: 0,
        startY: 0,
        startTranslateX: 0,
        startTranslateY: 0,
        pinchStartDistance: 0,
        pinchStartScale: 1,
      });
      this.applyZoomTransform(wrapper);
    }

    resetAllZoom() {
      this.querySelectorAll('.product-media__drag-zoom-wrapper').forEach(wrapper => {
        const state = this.zoomStates.get(wrapper) || this.initZoomState(wrapper);
        if (!state) return;
        state.scale = 1;
        state.translateX = 0;
        state.translateY = 0;
        this.applyZoomTransform(wrapper);
      });
      this.toggleZoomedState(false);
    }

    onPointerDown(event, wrapper) {
      if (event.pointerType === 'touch') return;
      const state = this.zoomStates.get(wrapper);
      if (!state || state.scale <= 1) return;
      wrapper.setPointerCapture(event.pointerId);
      state.isPanning = true;
      state.startX = event.clientX;
      state.startY = event.clientY;
      state.startTranslateX = state.translateX;
      state.startTranslateY = state.translateY;
    }

    onPointerMove(event, wrapper) {
      const state = this.zoomStates.get(wrapper);
      if (!state || !state.isPanning) return;
      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;
      state.translateX = state.startTranslateX + dx;
      state.translateY = state.startTranslateY + dy;
      this.clampZoomTranslate(wrapper);
      this.applyZoomTransform(wrapper);
    }

    onPointerUp(_event, wrapper) {
      const state = this.zoomStates.get(wrapper);
      if (!state) return;
      state.isPanning = false;
    }

    onTouchStart(event, wrapper) {
      const state = this.zoomStates.get(wrapper);
      if (!state) return;
      if (event.touches.length === 2) {
        state.pinchStartDistance = this.getTouchDistance(event.touches[0], event.touches[1]);
        state.pinchStartScale = state.scale;
      } else if (event.touches.length === 1 && state.scale > 1) {
        state.isPanning = true;
        state.startX = event.touches[0].clientX;
        state.startY = event.touches[0].clientY;
        state.startTranslateX = state.translateX;
        state.startTranslateY = state.translateY;
      }
    }

    onTouchMove(event, wrapper) {
      const state = this.zoomStates.get(wrapper);
      if (!state) return;
      if (event.touches.length === 2) {
        event.preventDefault();
        const distance = this.getTouchDistance(event.touches[0], event.touches[1]);
        const scale = state.pinchStartScale * (distance / state.pinchStartDistance);
        state.scale = Math.max(1, Math.min(scale, 3));
        this.toggleZoomedState(state.scale > 1);
        this.clampZoomTranslate(wrapper);
        this.applyZoomTransform(wrapper);
        return;
      }

      if (event.touches.length === 1 && state.isPanning && state.scale > 1) {
        event.preventDefault();
        const dx = event.touches[0].clientX - state.startX;
        const dy = event.touches[0].clientY - state.startY;
        state.translateX = state.startTranslateX + dx;
        state.translateY = state.startTranslateY + dy;
        this.clampZoomTranslate(wrapper);
        this.applyZoomTransform(wrapper);
      }
    }

    onTouchEnd(_event, wrapper) {
      const state = this.zoomStates.get(wrapper);
      if (!state) return;
      state.isPanning = false;
    }

    getTouchDistance(touchA, touchB) {
      const dx = touchA.clientX - touchB.clientX;
      const dy = touchA.clientY - touchB.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    clampZoomTranslate(wrapper) {
      const state = this.zoomStates.get(wrapper);
      if (!state) return;
      const img = wrapper.querySelector('img.product-media__image');
      if (!img) return;
      const bounds = this.getZoomBounds(wrapper, img, state.scale);
      state.translateX = Math.min(bounds.maxX, Math.max(bounds.minX, state.translateX));
      state.translateY = Math.min(bounds.maxY, Math.max(bounds.minY, state.translateY));
    }

    getZoomBounds(wrapper, img, scale) {
      const containerWidth = wrapper.clientWidth;
      const containerHeight = wrapper.clientHeight;
      const aspect = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
      let baseWidth = containerWidth;
      let baseHeight = containerWidth / aspect;
      if (baseHeight > containerHeight) {
        baseHeight = containerHeight;
        baseWidth = containerHeight * aspect;
      }
      const scaledWidth = baseWidth * scale;
      const scaledHeight = baseHeight * scale;
      const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
      const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);
      return { minX: -maxX, maxX, minY: -maxY, maxY };
    }

    applyZoomTransform(wrapper) {
      const state = this.zoomStates.get(wrapper);
      if (!state) return;
      wrapper.style.setProperty('--drag-zoom-scale', state.scale.toFixed(2));
      wrapper.style.setProperty('--drag-zoom-translate-x', `${state.translateX.toFixed(1)}px`);
      wrapper.style.setProperty('--drag-zoom-translate-y', `${state.translateY.toFixed(1)}px`);
    }

    loadHighResolutionImage(mediaContainer) {
      if (!mediaContainer.classList.contains('product-media-container--image')) return false;

      const image = mediaContainer.querySelector('img.product-media__image');
      if (!image || !(image instanceof HTMLImageElement)) return false;

      const highResolutionUrl = image.getAttribute('data_max_resolution');
      if (!highResolutionUrl || this.highResImagesLoaded.has(highResolutionUrl)) return false;

      this.preloadImage(highResolutionUrl);

      const newImage = new Image();
      newImage.className = image.className;
      newImage.alt = image.alt;
      newImage.setAttribute('data_max_resolution', highResolutionUrl);

      newImage.onload = () => {
        image.replaceWith(newImage);
        this.highResImagesLoaded.add(highResolutionUrl);
      };

      newImage.src = highResolutionUrl;
    }

    getMostVisibleElement(elements) {
      return new Promise((resolve) => {
        const observer = new IntersectionObserver(
          (entries) => {
            const mostVisible = entries.reduce((prev, current) =>
              current.intersectionRatio > prev.intersectionRatio ? current : prev
            );
            observer.disconnect();
            resolve(mostVisible.target);
          },
          { threshold: [0, 0.5, 1] }
        );
        for (const element of elements) {
          observer.observe(element);
        }
      });
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    supportsViewTransitions() {
      return 'startViewTransition' in document;
    }

    async startViewTransition(callback) {
      if (this.supportsViewTransitions()) {
        return document.startViewTransition(callback);
      } else {
        callback();
      }
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    preloadImage(src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    }

    onAnimationEnd(element) {
      return new Promise((resolve) => {
        const handleAnimationEnd = () => {
          element.removeEventListener('animationend', handleAnimationEnd);
          resolve();
        };
        element.addEventListener('animationend', handleAnimationEnd);
      });
    }

    scrollIntoView(element, { ancestor, behavior = 'smooth', block = 'start', inline = 'start' } = {}) {
      if (!ancestor) {
        return element.scrollIntoView({ behavior, block, inline });
      }

      const ancestorRect = ancestor.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const calculateScrollOffset = (alignment, ancestorStart, ancestorLength, elemStart, elemLength, currentScroll) => {
        switch (alignment) {
          case 'start':
            return currentScroll + elemStart - ancestorStart;
          case 'center':
            return currentScroll + elemStart - ancestorStart - ancestorLength / 2 + elemLength / 2;
          case 'end':
            return currentScroll + elemStart - ancestorStart - ancestorLength + elemLength;
          default:
            return currentScroll;
        }
      };

      const scrollTop =
        ancestor.scrollHeight > ancestor.clientHeight
          ? calculateScrollOffset(
              block,
              ancestorRect.top,
              ancestor.clientHeight,
              elementRect.top,
              elementRect.height,
              ancestor.scrollTop
          )
          : ancestor.scrollTop;

      const scrollLeft =
        ancestor.scrollWidth > ancestor.clientWidth
          ? Math.max(0, calculateScrollOffset(
              inline,
              ancestorRect.left,
              ancestor.clientWidth,
              elementRect.left,
              elementRect.width,
              ancestor.scrollLeft
            ))
          : ancestor.scrollLeft;

      ancestor.scrollTo({
        left: scrollLeft,
        top: scrollTop,
        behavior: behavior
      });
    }
  }

  if (!customElements.get('zoom-dialog')) {
    customElements.define('zoom-dialog', ZoomDialog);
  }
}

document.addEventListener('click', e => {
  const trigger = e.target.closest('[data-zoom-target]');
  if (!trigger) return;

  const targetSelector = trigger.dataset.zoomTarget;
  const zoomDialog = document.querySelector(targetSelector);
  if (!zoomDialog) return;

  const index = Number(trigger.dataset.zoomIndex);

  if (trigger.classList.contains('product-media-container--zoomable')) {
    zoomDialog.open(index, e);
  }

  if (trigger.classList.contains('dialog-thumbnails-list__thumbnail')) {
    zoomDialog.handleThumbnailClick(index);

    const swiperThumbnail = document.querySelector(`.swiper-controls__thumbnail[data-index="${index}"]`);
    if (swiperThumbnail) {
      swiperThumbnail.click();
    }
  }
});

if (!customElements.get('scroll-hint')) {
  class ScrollHint extends HTMLElement {
    connectedCallback() {
      this.addEventListener('scroll', theme.utils.rafThrottle(this.update.bind(this)));
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.update);
      this.resizeObserver.disconnect();
    }

    update = () => {
      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = this;
      const scrollDirection = scrollWidth > clientWidth ? 'horizontal' : 'vertical';
      const scrollPercentage =
        scrollDirection === 'vertical'
          ? scrollTop / (scrollHeight - clientHeight)
          : scrollLeft / (scrollWidth - clientWidth);

      this.style.maskImage = Number.isNaN(scrollPercentage)
        ? ''
        : `linear-gradient(
          to ${scrollDirection === 'vertical' ? 'bottom' : 'right'},
          transparent ${scrollPercentage > 0 ? 1 : 0}%,
          black ${scrollPercentage < 0.1 ? scrollPercentage * 100 : 10}%,
          black ${scrollPercentage > 0.9 ? scrollPercentage * 100 : 90}%,
          transparent 100%
        )`;
    };

    resizeObserver = new ResizeObserver(this.update);
  }

  customElements.define('scroll-hint', ScrollHint);
}
